import { useState } from "react";
import { CalendarDays } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const isoDay = (date) => `${date.getFullYear()}-`
  + `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const parse = (value) => {
  if (!value || value.length < 10) return undefined;
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const pretty = (value) => {
  const d = parse(value);
  return d ? `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : "";
};

/** A calendar date picker that reads and writes a plain YYYY-MM-DD string. */
export const DateField = ({ value, onChange, placeholder = "Pick a date", testId,
                            className, clearable = true, disabled }) => {
  const [open, setOpen] = useState(false);
  const selected = parse(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testId}
                className={cn("w-full justify-start gap-2 border-border bg-muted/40 font-normal",
                              !value && "text-muted-foreground", className)}>
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="truncate">{value ? pretty(value) : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto border-border bg-card p-0">
        <Calendar mode="single" selected={selected} defaultMonth={selected} initialFocus
                  disabled={disabled}
                  onSelect={(d) => {
                    if (!d && !clearable) { setOpen(false); return; }
                    onChange(d ? isoDay(d) : "");
                    setOpen(false);
                  }} />
        {clearable && value ? (
          <div className="border-t border-border p-2">
            <Button size="sm" variant="ghost"
                    data-testid={testId ? `${testId}-clear` : undefined}
                    onClick={() => { onChange(""); setOpen(false); }}
                    className="h-7 w-full rounded-full text-[11px] text-muted-foreground">
              Clear
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
